import type { ReactNode } from 'react';

import { DatadogProvider } from './DatadogProvider';
import { PostHogClientProvider } from './PostHogClientProvider';

/**
 * The React half of this adapter — the vendors that need to be mounted, not merely called.
 *
 * `OnboardingRoot` renders whatever `vendors.Provider` it was given and knows nothing about
 * what is inside, which is what keeps the vendor SDKs out of the core graph. Two of these
 * vendors genuinely need a component:
 *
 * - `DatadogProvider` — native wraps `@datadog/mobile-react-native`'s own provider and
 *   enables Session Replay on init; web initializes the browser SDKs from an effect.
 * - `PostHogClientProvider` — native mounts the RN `PostHogProvider` so touch autocapture
 *   is active; web just inits the `posthog-js` singleton, which captures through a global.
 *
 * Screen views are NOT here. They come from `root`'s `ScreenViewTracker`, mounted by
 * `OnboardingRoot` and gated by its `trackScreens` prop, because whether we own onboarding pageviews is the
 * caller's decision and not a vendor detail — see `ScreenViewTracker`.
 *
 * Datadog is outermost, matching the nesting this replaced, so RUM is
 * initialized before any PostHog work can throw inside it.
 */
export const VidaVendorProvider = ({ children }: { children: ReactNode }) => {
  return (
    <DatadogProvider>
      <PostHogClientProvider>{children}</PostHogClientProvider>
    </DatadogProvider>
  );
};
